import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Space,
    message,
    Popconfirm,
    Tag,
    Row,
    Col,
    Switch,
    InputNumber,
    Drawer,
    Tabs,
    Descriptions,
    Divider,
    Typography,
    Alert,
    Card,
    Tooltip,
    Badge
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    DownloadOutlined,
    UploadOutlined,
    SyncOutlined,
    CarOutlined,
    ThunderboltOutlined,
    EyeOutlined,
    SafetyCertificateOutlined,
    UserOutlined,
    MinusCircleOutlined,
    FilterOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import Layout from './Layout';

const { TextArea } = Input;
const { Option } = Select;
const { Text, Title } = Typography;

const TYPE_OPTIONS = [
    { value: 'customer', label: 'Заказчик', color: 'blue' },
    { value: 'executor', label: 'Исполнитель', color: 'green' },
    { value: 'vehicle', label: 'Автомобиль', color: 'purple' },
    { value: 'generator', label: 'Генератор', color: 'orange' }
];

const TYPE_LABELS = Object.fromEntries(
    TYPE_OPTIONS.map(item => [item.value, item.label])
);
const TYPE_COLORS = Object.fromEntries(
    TYPE_OPTIONS.map(item => [item.value, item.color])
);

const emptyFormDefaults = {
    type: 'customer',
    has_personal_car: false,
    has_own_fuel: false,
    allowed_articles: [],
    price_lists: [],
    executor_user_ids: []
};

const dash = value =>
    value === null ||
    value === undefined ||
    String(value).trim() === ''
        ? 'не заполнены'
        : value;

/**
 * Построчный выбор исполнителей + подсказка занятости.
 */
const ExecutorRowsEditor = ({
    value,
    onChange,
    users = [],
    excludeCounterpartyId = null
}) => {
    const rows =
        Array.isArray(value) && value.length > 0
            ? value
            : [undefined];

    const emit = next => {
        if (onChange) onChange(next);
    };

    const setRow = (index, userId) => {
        const next = [...rows];
        next[index] = userId;
        emit(next);
    };

    const addRow = () => {
        emit([...rows, undefined]);
    };

    const removeRow = index => {
        const next = rows.filter((_, i) => i !== index);
        emit(next.length ? next : [undefined]);
    };

    const selectedElsewhere = new Set(
        rows.filter(
            (id, index) =>
                id != null &&
                rows.findIndex(v => v === id) !== index
        )
    );

    return (
        <div>
            <Space
                direction="vertical"
                style={{ width: '100%' }}
                size={10}
            >
                {rows.map((userId, index) => {
                    const user = users.find(
                        u => u.id === userId
                    );
                    const busyElsewhere = (
                        user?.busyAt || []
                    ).filter(
                        cp =>
                            String(cp.id) !==
                            String(
                                excludeCounterpartyId || ''
                            )
                    );
                    const duplicate =
                        userId != null &&
                        selectedElsewhere.has(userId);

                    return (
                        <div key={`exec-row-${index}`}>
                            <Space
                                align="start"
                                style={{ width: '100%' }}
                            >
                                <Select
                                    showSearch
                                    allowClear
                                    placeholder={`Исполнитель ${index + 1}`}
                                    style={{
                                        width: 420,
                                        maxWidth: '100%'
                                    }}
                                    value={userId}
                                    optionFilterProp="label"
                                    options={users.map(u => {
                                        const busy = (
                                            u.busyAt || []
                                        ).filter(
                                            cp =>
                                                String(
                                                    cp.id
                                                ) !==
                                                String(
                                                    excludeCounterpartyId ||
                                                        ''
                                                )
                                        );
                                        return {
                                            value: u.id,
                                            label: busy.length
                                                ? `${u.label || u.name} — занят`
                                                : u.label ||
                                                  `${u.name} (${u.login})`
                                        };
                                    })}
                                    onChange={val =>
                                        setRow(index, val)
                                    }
                                />
                                <Button
                                    type="text"
                                    danger
                                    icon={
                                        <MinusCircleOutlined />
                                    }
                                    onClick={() =>
                                        removeRow(index)
                                    }
                                    disabled={
                                        rows.length === 1 &&
                                        userId == null
                                    }
                                />
                            </Space>
                            {duplicate ? (
                                <Text
                                    type="danger"
                                    style={{ fontSize: 12 }}
                                >
                                    Этот исполнитель уже
                                    выбран в другой строке
                                </Text>
                            ) : null}
                            {busyElsewhere.length ? (
                                <div style={{ marginTop: 4 }}>
                                    <Tag color="orange">
                                        Занят:{' '}
                                        {busyElsewhere
                                            .map(cp => cp.name)
                                            .join(', ')}
                                    </Tag>
                                </div>
                            ) : null}
                        </div>
                    );
                })}
            </Space>
            <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={addRow}
                style={{ marginTop: 12 }}
                block
            >
                Добавить исполнителя
            </Button>
        </div>
    );
};

const CounterpartiesPage = () => {
    const [counterparties, setCounterparties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchDraft, setSearchDraft] = useState('');
    const [typeFilter, setTypeFilter] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });
    const [sortParams, setSortParams] = useState({
        field: 'name',
        order: 'asc'
    });

    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerRecord, setDrawerRecord] = useState(null);
    const [drawerLoading, setDrawerLoading] = useState(false);

    const [innChecking, setInnChecking] = useState(false);
    const [innCheckResult, setInnCheckResult] = useState(null);

    const [options, setOptions] = useState({
        articles: [],
        priceTypes: [],
        vehicles: [],
        generators: [],
        users: []
    });

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`
    });

    const fetchCounterparties = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                search: searchText,
                sortField: sortParams.field,
                sortOrder: sortParams.order
            };
            if (typeFilter.length) {
                params.type = typeFilter.join(',');
            }

            const response = await axios.get('/api/counterparties', {
                params,
                headers: authHeaders()
            });

            setCounterparties(response.data.data || []);
            setPagination(prev => ({
                ...prev,
                total: response.data.total || 0
            }));
            setOptions(
                response.data.options || {
                    articles: [],
                    priceTypes: [],
                    vehicles: [],
                    generators: [],
                    users: []
                }
            );
        } catch (error) {
            message.error('Ошибка при загрузке контрагентов');
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [
        pagination.current,
        pagination.pageSize,
        searchText,
        sortParams,
        typeFilter
    ]);

    useEffect(() => {
        fetchCounterparties();
    }, [fetchCounterparties]);

    const handleTableChange = (p, _filters, sorter) => {
        setPagination(p);
        if (sorter.field) {
            setSortParams({
                field: sorter.field,
                order:
                    sorter.order === 'descend' ? 'desc' : 'asc'
            });
        }
    };

    const handleSearch = value => {
        const next = value || '';
        setSearchDraft(next);
        setSearchText(next);
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const openDrawer = async record => {
        setDrawerOpen(true);
        setDrawerLoading(true);
        setInnCheckResult(null);
        try {
            const { data } = await axios.get(
                `/api/counterparties/${record.id}`,
                { headers: authHeaders() }
            );
            setDrawerRecord(data);
        } catch (error) {
            setDrawerRecord(record);
            message.error('Не удалось загрузить карточку');
        } finally {
            setDrawerLoading(false);
        }
    };

    const FORM_FIELD_KEYS = [
        'name',
        'type',
        'comment',
        'allowed_articles',
        'price_lists',
        'inn',
        'kpp',
        'ogrn',
        'legal_name',
        'legal_address',
        'bank_name',
        'bik',
        'checking_account',
        'correspondent_account',
        'contract_number',
        'service_vehicle_id',
        'large_generator_id',
        'small_generator1_id',
        'small_generator2_id',
        'has_personal_car',
        'has_own_fuel',
        'fuel_consumption',
        'executor_user_ids',
        'am_login',
        'am_name'
    ];

    const pickFormValues = record => {
        const values = { ...emptyFormDefaults };
        if (!record) return values;
        for (const key of FORM_FIELD_KEYS) {
            if (record[key] !== undefined) {
                values[key] = record[key];
            }
        }
        values.allowed_articles = record.allowed_articles || [];
        values.price_lists = record.price_lists || [];
        values.executor_user_ids = Array.isArray(
            record.executor_user_ids
        )
            ? record.executor_user_ids
            : [];
        values.has_personal_car = Boolean(
            record.has_personal_car
        );
        values.has_own_fuel = Boolean(record.has_own_fuel);
        return values;
    };

    const openModal = async (record = null) => {
        setInnCheckResult(null);
        if (record?.id) {
            setEditingRecord(record);
            form.setFieldsValue(pickFormValues(record));
            setModalVisible(true);
            /*
             * Подтягиваем полную карточку с сервера,
             * чтобы в форме не было «дыр» из строки таблицы.
             */
            try {
                const { data } = await axios.get(
                    `/api/counterparties/${record.id}`,
                    { headers: authHeaders() }
                );
                setEditingRecord(data);
                form.setFieldsValue(pickFormValues(data));
            } catch (error) {
                console.error(error);
            }
            return;
        }

        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({ ...emptyFormDefaults });
        setModalVisible(true);
    };

    const buildSavePayload = (forceBusy = false) => {
        const values = form.getFieldsValue(true);
        const base = editingRecord
            ? pickFormValues(editingRecord)
            : { ...emptyFormDefaults };

        const payload = {};
        for (const key of FORM_FIELD_KEYS) {
            const fromForm = values[key];
            if (fromForm !== undefined) {
                payload[key] = fromForm;
            } else if (base[key] !== undefined) {
                payload[key] = base[key];
            }
        }

        payload.executor_user_ids = [
            ...new Set(
                (Array.isArray(values.executor_user_ids)
                    ? values.executor_user_ids
                    : base.executor_user_ids || []
                ).filter(id => id != null && id !== '')
            )
        ];

        if (!Array.isArray(payload.allowed_articles)) {
            payload.allowed_articles = base.allowed_articles || [];
        }
        if (!Array.isArray(payload.price_lists)) {
            payload.price_lists = base.price_lists || [];
        }

        payload.has_personal_car = Boolean(
            payload.has_personal_car
        );
        payload.has_own_fuel = Boolean(payload.has_own_fuel);
        payload.force_busy_executors = Boolean(forceBusy);

        return payload;
    };

    const handleFormSubmit = async (forceBusy = false) => {
        try {
            await form.validateFields(['name', 'type']);
            const payload = buildSavePayload(forceBusy);
            setLoading(true);

            try {
                if (editingRecord) {
                    await axios.put(
                        `/api/counterparties/${editingRecord.id}`,
                        payload,
                        { headers: authHeaders() }
                    );
                    message.success(
                        'Контрагент успешно обновлен'
                    );
                } else {
                    await axios.post(
                        '/api/counterparties',
                        payload,
                        { headers: authHeaders() }
                    );
                    message.success(
                        'Контрагент успешно добавлен'
                    );
                }

                setModalVisible(false);
                fetchCounterparties();
                if (drawerOpen && editingRecord) {
                    openDrawer({ id: editingRecord.id });
                }
            } catch (error) {
                if (
                    error.response?.status === 409 &&
                    error.response?.data?.code ===
                        'EXECUTOR_BUSY'
                ) {
                    const conflicts =
                        error.response.data.conflicts || [];
                    const lines = conflicts
                        .map(c => {
                            const user = (
                                options.users || []
                            ).find(u => u.id === c.userId);
                            const names = (
                                c.counterparties || []
                            )
                                .map(cp => cp.name)
                                .join(', ');
                            return `• ${user?.name || user?.login || c.userId}: ${names}`;
                        })
                        .join('\n');

                    Modal.confirm({
                        title: 'Исполнители уже заняты',
                        content: (
                            <div>
                                <p>
                                    Эти пользователи уже
                                    привязаны к другим
                                    контрагентам:
                                </p>
                                <pre
                                    style={{
                                        whiteSpace:
                                            'pre-wrap',
                                        fontSize: 12
                                    }}
                                >
                                    {lines}
                                </pre>
                                <p>
                                    Всё равно сохранить
                                    привязку?
                                </p>
                            </div>
                        ),
                        okText: 'Сохранить всё равно',
                        cancelText: 'Отмена',
                        onOk: () => handleFormSubmit(true)
                    });
                    return;
                }
                message.error(
                    error.response?.data?.message ||
                        'Ошибка при сохранении'
                );
            }
        } catch (error) {
            if (!error?.errorFields) {
                message.error('Ошибка при сохранении');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async id => {
        try {
            await axios.delete(`/api/counterparties/${id}`, {
                headers: authHeaders()
            });
            message.success('Контрагент удален');
            if (drawerRecord?.id === id) {
                setDrawerOpen(false);
                setDrawerRecord(null);
            }
            fetchCounterparties();
        } catch (error) {
            message.error('Ошибка при удалении');
        }
    };

    const getTypeLabel = type => TYPE_LABELS[type] || type;

    const getTypeValue = label => {
        const found = TYPE_OPTIONS.find(
            item => item.label === label
        );
        return found?.value || 'customer';
    };

    const checkInn = async ({
        inn,
        apply = false,
        counterpartyId = null,
        fillForm = false
    }) => {
        const clean = String(inn || '').replace(/\D/g, '');
        if (!clean) {
            message.warning('Укажите ИНН');
            return null;
        }

        setInnChecking(true);
        try {
            const { data } = await axios.post(
                '/api/counterparties/check-inn',
                {
                    inn: clean,
                    id: counterpartyId || undefined,
                    apply
                },
                { headers: authHeaders() }
            );

            setInnCheckResult(data);

            if (fillForm && data.found) {
                form.setFieldsValue({
                    inn: data.inn || clean,
                    legal_name:
                        data.legal_name ||
                        form.getFieldValue('legal_name'),
                    kpp: data.kpp || form.getFieldValue('kpp'),
                    ogrn:
                        data.ogrn || form.getFieldValue('ogrn'),
                    legal_address:
                        data.legal_address ||
                        form.getFieldValue('legal_address')
                });
                message.success(
                    'Реквизиты подставлены из ЕГРЮЛ'
                );
            } else if (data.status === 'invalid') {
                message.error(data.message || 'Неверный ИНН');
            } else if (data.status === 'not_found') {
                message.warning(
                    data.message || 'Не найдено в ЕГРЮЛ'
                );
            } else if (data.status === 'error') {
                message.error(
                    data.message || 'Ошибка проверки ФНС'
                );
            } else if (data.found) {
                message.success(
                    data.message || 'Контрагент найден'
                );
            }

            if (apply && data.counterparty) {
                setDrawerRecord(prev => ({
                    ...prev,
                    ...data.counterparty
                }));
                fetchCounterparties();
            }

            return data;
        } catch (error) {
            message.error(
                error.response?.data?.message ||
                    'Ошибка проверки ИНН'
            );
            return null;
        } finally {
            setInnChecking(false);
        }
    };

    const handleExport = async () => {
        const hide = message.loading('Формирование Excel...', 0);
        try {
            const res = await axios.get('/api/counterparties', {
                params: {
                    limit: 10000,
                    type: typeFilter.length
                        ? typeFilter.join(',')
                        : undefined,
                    search: searchText || undefined
                },
                headers: authHeaders()
            });

            const data = (res.data.data || []).map(c => ({
                ID: c.id,
                Название: c.name,
                Тип: getTypeLabel(c.type),
                ИНН: c.inn || '',
                КПП: c.kpp || '',
                ОГРН: c.ogrn || '',
                'Юр. наименование': c.legal_name || '',
                'Юр. адрес': c.legal_address || '',
                Банк: c.bank_name || '',
                БИК: c.bik || '',
                'Р/с': c.checking_account || '',
                'К/с': c.correspondent_account || '',
                Договор: c.contract_number || '',
                Исполнители: (c.executors || [])
                    .map(u => u.login)
                    .join(', '),
                'Служебный автомобиль':
                    c.service_vehicle?.name || '',
                'ДГУ Большой': c.large_generator?.name || '',
                'ДГУ Малый 1': c.small_generator1?.name || '',
                'ДГУ Малый 2': c.small_generator2?.name || '',
                'Личный авто': c.has_personal_car ? 'Да' : 'Нет',
                'Своё топливо': c.has_own_fuel ? 'Да' : 'Нет',
                'Расход топлива': c.fuel_consumption || '',
                'Разрешённые статьи':
                    c.allowed_articles
                        ?.map(
                            id =>
                                options.articles.find(
                                    a => a.id === id
                                )?.name || id
                        )
                        .join(', ') || '',
                'Прайс листы':
                    c.price_lists
                        ?.map(
                            id =>
                                options.priceTypes.find(
                                    t => t.id === id
                                )?.name || id
                        )
                        .join(', ') || '',
                Комментарий: c.comment || '',
                'Логин Active Map': c.am_login || '',
                'Имя в Active Map': c.am_name || '',
                Удалить: 'Нет'
            }));

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Контрагенты');
            const excelBuffer = XLSX.write(wb, {
                bookType: 'xlsx',
                type: 'array'
            });
            saveAs(
                new Blob([excelBuffer]),
                'контрагенты_экспорт.xlsx'
            );
            hide();
        } catch (e) {
            hide();
            message.error('Ошибка экспорта');
        }
    };

    const handleImport = async e => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async evt => {
            try {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, {
                    type: 'array'
                });
                const jsonData = XLSX.utils.sheet_to_json(
                    workbook.Sheets[workbook.SheetNames[0]]
                );

                const transformed = jsonData.map(row => ({
                    name: row['Название'],
                    type: getTypeValue(row['Тип']),
                    inn: row['ИНН'],
                    kpp: row['КПП'],
                    ogrn: row['ОГРН'],
                    legal_name: row['Юр. наименование'],
                    legal_address: row['Юр. адрес'],
                    bank_name: row['Банк'],
                    bik: row['БИК'],
                    checking_account: row['Р/с'],
                    correspondent_account: row['К/с'],
                    contract_number: row['Договор'],
                    executors: row['Исполнители'],
                    service_vehicle_name:
                        row['Служебный автомобиль'],
                    large_generator_name: row['ДГУ Большой'],
                    small_generator1_name: row['ДГУ Малый 1'],
                    small_generator2_name: row['ДГУ Малый 2'],
                    has_personal_car: row['Личный авто'] === 'Да',
                    has_own_fuel: row['Своё топливо'] === 'Да',
                    fuel_consumption: row['Расход топлива'],
                    allowed_articles: String(
                        row['Разрешённые статьи'] || ''
                    )
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean),
                    price_lists: String(row['Прайс листы'] || '')
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean),
                    comment: row['Комментарий'],
                    am_login:
                        row['Логин Active Map'] ||
                        row['Логин AM'],
                    am_name:
                        row['Имя в Active Map'] ||
                        row['Имя в AM'],
                    delete_record:
                        row['Удалить'] === 'Да' ||
                        row['Удалить'] === 1
                }));

                await axios.post(
                    '/api/counterparties/import',
                    transformed,
                    { headers: authHeaders() }
                );
                message.success('Импорт завершен успешно');
                fetchCounterparties();
            } catch (err) {
                message.error('Ошибка при обработке файла');
            }
        };
        reader.readAsArrayBuffer(file);
        e.target.value = '';
    };

    const renderInnStatus = record => {
        const status = record?.inn_check_status;
        if (!status) return <Text type="secondary">не проверялся</Text>;
        const map = {
            valid: { color: 'success', text: 'найден в ЕГРЮЛ' },
            invalid: { color: 'error', text: 'неверный ИНН' },
            not_found: {
                color: 'warning',
                text: 'не найден'
            },
            error: { color: 'error', text: 'ошибка ФНС' }
        };
        const meta = map[status] || {
            color: 'default',
            text: status
        };
        return <Tag color={meta.color}>{meta.text}</Tag>;
    };

    const columns = [
        {
            title: 'Контрагент',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            fixed: 'left',
            width: 280,
            render: (name, record) => (
                <div
                    style={{ minWidth: 0 }}
                    onClick={e => e.stopPropagation()}
                >
                    <Button
                        type="link"
                        style={{
                            padding: 0,
                            height: 'auto',
                            fontWeight: 600,
                            fontSize: 14,
                            whiteSpace: 'normal',
                            textAlign: 'left'
                        }}
                        onClick={() => openDrawer(record)}
                    >
                        {name}
                    </Button>
                    <div
                        style={{
                            marginTop: 2,
                            display: 'flex',
                            gap: 8,
                            flexWrap: 'wrap',
                            alignItems: 'center'
                        }}
                    >
                        <Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                        >
                            #{record.id}
                        </Text>
                        {record.legal_name &&
                        record.legal_name !== name ? (
                            <Text
                                type="secondary"
                                style={{
                                    fontSize: 12,
                                    maxWidth: 220
                                }}
                                ellipsis={{
                                    tooltip: record.legal_name
                                }}
                            >
                                {record.legal_name}
                            </Text>
                        ) : null}
                    </div>
                </div>
            )
        },
        {
            title: 'Тип',
            dataIndex: 'type',
            key: 'type',
            width: 130,
            render: type => (
                <Tag
                    color={TYPE_COLORS[type]}
                    style={{ margin: 0 }}
                >
                    {getTypeLabel(type)}
                </Tag>
            )
        },
        {
            title: 'ИНН',
            dataIndex: 'inn',
            key: 'inn',
            width: 150,
            sorter: true,
            render: (inn, record) => {
                if (!inn) {
                    return (
                        <Text type="secondary">не указан</Text>
                    );
                }
                const statusColor = {
                    valid: '#52c41a',
                    invalid: '#ff4d4f',
                    not_found: '#faad14',
                    error: '#ff4d4f'
                }[record.inn_check_status];
                return (
                    <Space size={6}>
                        <Text
                            style={{
                                fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, monospace',
                                letterSpacing: 0.2
                            }}
                        >
                            {inn}
                        </Text>
                        {statusColor ? (
                            <Tooltip
                                title={
                                    record.inn_check_status ===
                                    'valid'
                                        ? 'Проверен в ЕГРЮЛ'
                                        : record.inn_check_status ===
                                            'not_found'
                                          ? 'Не найден в ЕГРЮЛ'
                                          : 'Ошибка / неверный ИНН'
                                }
                            >
                                <Badge
                                    color={statusColor}
                                    style={{ marginBottom: 2 }}
                                />
                            </Tooltip>
                        ) : null}
                    </Space>
                );
            }
        },
        {
            title: 'Исполнители',
            key: 'executors',
            width: 220,
            render: (_, r) => {
                if (!r.executors?.length) {
                    return (
                        <Text type="secondary">не назначены</Text>
                    );
                }
                return (
                    <Space size={[4, 4]} wrap>
                        {r.executors.slice(0, 2).map(u => (
                            <Tooltip
                                key={u.id}
                                title={u.login}
                            >
                                <Tag
                                    style={{ margin: 0 }}
                                    icon={<UserOutlined />}
                                >
                                    {u.name || u.login}
                                </Tag>
                            </Tooltip>
                        ))}
                        {r.executors.length > 2 ? (
                            <Tooltip
                                title={r.executors
                                    .slice(2)
                                    .map(
                                        u =>
                                            u.name || u.login
                                    )
                                    .join(', ')}
                            >
                                <Tag style={{ margin: 0 }}>
                                    +{r.executors.length - 2}
                                </Tag>
                            </Tooltip>
                        ) : null}
                    </Space>
                );
            }
        },
        {
            title: 'Техника',
            key: 'tech',
            width: 160,
            render: (_, r) => {
                const items = [];
                if (r.service_vehicle?.name) {
                    items.push(
                        <Tooltip
                            key="car"
                            title={`Авто: ${r.service_vehicle.name}`}
                        >
                            <Tag
                                icon={<CarOutlined />}
                                style={{ margin: 0 }}
                            >
                                авто
                            </Tag>
                        </Tooltip>
                    );
                }
                if (
                    r.large_generator?.name ||
                    r.small_generator1?.name ||
                    r.small_generator2?.name
                ) {
                    const dgu = [
                        r.large_generator?.name &&
                            `Б: ${r.large_generator.name}`,
                        r.small_generator1?.name &&
                            `М1: ${r.small_generator1.name}`,
                        r.small_generator2?.name &&
                            `М2: ${r.small_generator2.name}`
                    ]
                        .filter(Boolean)
                        .join(' · ');
                    items.push(
                        <Tooltip key="dgu" title={dgu}>
                            <Tag
                                icon={<ThunderboltOutlined />}
                                color="gold"
                                style={{ margin: 0 }}
                            >
                                ДГУ
                            </Tag>
                        </Tooltip>
                    );
                }
                if (r.has_personal_car) {
                    items.push(
                        <Tag
                            key="personal"
                            color="cyan"
                            style={{ margin: 0 }}
                        >
                            личный
                        </Tag>
                    );
                }
                if (r.has_own_fuel) {
                    items.push(
                        <Tooltip
                            key="fuel"
                            title={
                                r.fuel_consumption
                                    ? `Расход: ${r.fuel_consumption} л`
                                    : 'Своё топливо'
                            }
                        >
                            <Tag
                                color="magenta"
                                style={{ margin: 0 }}
                            >
                                ГСМ
                            </Tag>
                        </Tooltip>
                    );
                }
                if (!items.length) {
                    return <Text type="secondary">—</Text>;
                }
                return (
                    <Space size={[4, 4]} wrap>
                        {items}
                    </Space>
                );
            }
        },
        {
            title: '',
            key: 'actions',
            fixed: 'right',
            width: 118,
            render: (_, record) => (
                <Space
                    size={4}
                    onClick={e => e.stopPropagation()}
                >
                    <Tooltip title="Просмотр">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => openDrawer(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Редактировать">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Удалить контрагента?"
                        description={record.name}
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                        onConfirm={() =>
                            handleDelete(record.id)
                        }
                    >
                        <Tooltip title="Удалить">
                            <Button
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const innAlert = innCheckResult ? (
        <Alert
            style={{ marginBottom: 12 }}
            type={
                innCheckResult.status === 'valid'
                    ? 'success'
                    : innCheckResult.status === 'not_found'
                      ? 'warning'
                      : 'error'
            }
            showIcon
            message={innCheckResult.message}
            description={
                innCheckResult.found ? (
                    <div>
                        <div>
                            <b>{innCheckResult.legal_name}</b>
                        </div>
                        {innCheckResult.ogrn && (
                            <div>ОГРН: {innCheckResult.ogrn}</div>
                        )}
                        {innCheckResult.kpp && (
                            <div>КПП: {innCheckResult.kpp}</div>
                        )}
                        {innCheckResult.legal_address && (
                            <div>
                                {innCheckResult.legal_address}
                            </div>
                        )}
                    </div>
                ) : null
            }
        />
    ) : null;

    const activeFilterCount = typeFilter.length + (searchText ? 1 : 0);

    return (
        <Layout>
            <div
                style={{
                    padding: '20px 24px 32px',
                    background: '#f5f7fa',
                    minHeight: '100%'
                }}
            >
                <style>{`
                    .cp-table .ant-table-thead > tr > th {
                        background: #fafbfc !important;
                        font-weight: 600;
                        color: #334155;
                    }
                    .cp-table .ant-table-tbody > tr.cp-row:hover > td {
                        background: #f0f7ff !important;
                    }
                    .cp-table .ant-table-tbody > tr > td {
                        vertical-align: middle;
                    }
                `}</style>

                <Card
                    bordered={false}
                    style={{
                        marginBottom: 16,
                        borderRadius: 12,
                        boxShadow:
                            '0 1px 2px rgba(15, 23, 42, 0.04)'
                    }}
                    bodyStyle={{ padding: '16px 20px' }}
                >
                    <Row
                        gutter={[12, 12]}
                        align="middle"
                        justify="space-between"
                    >
                        <Col flex="auto">
                            <Space
                                align="baseline"
                                size={12}
                                wrap
                            >
                                <Title
                                    level={3}
                                    style={{ margin: 0 }}
                                >
                                    Контрагенты
                                </Title>
                                <Text type="secondary">
                                    {pagination.total
                                        ? `${pagination.total} записей`
                                        : 'справочник КА'}
                                </Text>
                            </Space>
                        </Col>
                        <Col>
                            <Space wrap>
                                <Button
                                    icon={<DownloadOutlined />}
                                    onClick={handleExport}
                                >
                                    Экспорт
                                </Button>
                                <Button
                                    icon={<UploadOutlined />}
                                    onClick={() =>
                                        document
                                            .getElementById(
                                                'import-file'
                                            )
                                            .click()
                                    }
                                >
                                    Импорт
                                </Button>
                                <input
                                    id="import-file"
                                    type="file"
                                    style={{ display: 'none' }}
                                    onChange={handleImport}
                                    accept=".xlsx, .xls"
                                />
                                <Button
                                    icon={<SyncOutlined />}
                                    onClick={() =>
                                        fetchCounterparties()
                                    }
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() => openModal()}
                                >
                                    Добавить
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>

                <Card
                    bordered={false}
                    style={{
                        borderRadius: 12,
                        boxShadow:
                            '0 1px 2px rgba(15, 23, 42, 0.04)'
                    }}
                    bodyStyle={{ padding: 0 }}
                >
                    <div
                        style={{
                            padding: '14px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            background: '#fff'
                        }}
                    >
                        <Row gutter={[12, 12]} align="middle">
                            <Col xs={24} md={10} lg={8}>
                                <Input.Search
                                    placeholder="Поиск по названию, ИНН, Active Map…"
                                    value={searchDraft}
                                    onChange={e =>
                                        setSearchDraft(
                                            e.target.value
                                        )
                                    }
                                    onSearch={handleSearch}
                                    allowClear
                                    enterButton
                                />
                            </Col>
                            <Col xs={24} md={8} lg={7}>
                                <Select
                                    mode="multiple"
                                    allowClear
                                    placeholder={
                                        <span>
                                            <FilterOutlined />{' '}
                                            Тип контрагента
                                        </span>
                                    }
                                    style={{ width: '100%' }}
                                    value={typeFilter}
                                    options={TYPE_OPTIONS}
                                    maxTagCount="responsive"
                                    onChange={values => {
                                        setTypeFilter(values);
                                        setPagination(prev => ({
                                            ...prev,
                                            current: 1
                                        }));
                                    }}
                                />
                            </Col>
                            <Col flex="auto">
                                {activeFilterCount > 0 ? (
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setTypeFilter([]);
                                            setSearchDraft('');
                                            setSearchText('');
                                            setPagination(
                                                prev => ({
                                                    ...prev,
                                                    current: 1
                                                })
                                            );
                                        }}
                                    >
                                        Сбросить фильтры (
                                        {activeFilterCount})
                                    </Button>
                                ) : (
                                    <Text type="secondary">
                                        Клик по строке —
                                        просмотр карточки
                                    </Text>
                                )}
                            </Col>
                        </Row>
                    </div>

                    <Table
                        className="cp-table"
                        columns={columns}
                        dataSource={counterparties}
                        rowKey="id"
                        loading={loading}
                        size="middle"
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: [
                                '10',
                                '20',
                                '50',
                                '100'
                            ],
                            showTotal: total =>
                                `Всего: ${total}`,
                            style: { padding: '0 16px' }
                        }}
                        onChange={handleTableChange}
                        scroll={{ x: 1100 }}
                        rowClassName={() => 'cp-row'}
                        onRow={record => ({
                            onClick: () => openDrawer(record),
                            style: { cursor: 'pointer' }
                        })}
                    />
                </Card>

                <Drawer
                    title={
                        drawerRecord
                            ? drawerRecord.name
                            : 'Контрагент'
                    }
                    placement="right"
                    width={520}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    extra={
                        drawerRecord ? (
                            <Space>
                                <Button
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                        openModal(drawerRecord)
                                    }
                                >
                                    Изменить
                                </Button>
                            </Space>
                        ) : null
                    }
                >
                    {drawerLoading ? (
                        <Text type="secondary">Загрузка…</Text>
                    ) : drawerRecord ? (
                        <>
                            <Space style={{ marginBottom: 12 }}>
                                <Tag
                                    color={
                                        TYPE_COLORS[
                                            drawerRecord.type
                                        ]
                                    }
                                >
                                    {getTypeLabel(
                                        drawerRecord.type
                                    )}
                                </Tag>
                                {renderInnStatus(drawerRecord)}
                            </Space>

                            <Descriptions
                                size="small"
                                column={1}
                                bordered
                                title="Основное"
                            >
                                <Descriptions.Item label="ID">
                                    {drawerRecord.id}
                                </Descriptions.Item>
                                <Descriptions.Item label="Название">
                                    {drawerRecord.name}
                                </Descriptions.Item>
                                <Descriptions.Item label="Комментарий">
                                    {dash(drawerRecord.comment)}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider />

                            <Space
                                style={{
                                    width: '100%',
                                    justifyContent:
                                        'space-between',
                                    marginBottom: 8
                                }}
                            >
                                <Text strong>Реквизиты</Text>
                                <Button
                                    size="small"
                                    icon={
                                        <SafetyCertificateOutlined />
                                    }
                                    loading={innChecking}
                                    disabled={!drawerRecord.inn}
                                    onClick={() =>
                                        checkInn({
                                            inn: drawerRecord.inn,
                                            counterpartyId:
                                                drawerRecord.id,
                                            apply: true
                                        })
                                    }
                                >
                                    Проверить по ИНН
                                </Button>
                            </Space>
                            {innAlert}
                            <Descriptions
                                size="small"
                                column={1}
                                bordered
                            >
                                <Descriptions.Item label="ИНН">
                                    {dash(drawerRecord.inn)}
                                </Descriptions.Item>
                                <Descriptions.Item label="КПП">
                                    {dash(drawerRecord.kpp)}
                                </Descriptions.Item>
                                <Descriptions.Item label="ОГРН">
                                    {dash(drawerRecord.ogrn)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Юр. наименование">
                                    {dash(
                                        drawerRecord.legal_name
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Юр. адрес">
                                    {dash(
                                        drawerRecord.legal_address
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Банк">
                                    {dash(drawerRecord.bank_name)}
                                </Descriptions.Item>
                                <Descriptions.Item label="БИК">
                                    {dash(drawerRecord.bik)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Р/с">
                                    {dash(
                                        drawerRecord.checking_account
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="К/с">
                                    {dash(
                                        drawerRecord.correspondent_account
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Договор">
                                    {dash(
                                        drawerRecord.contract_number
                                    )}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider />
                            <Descriptions
                                size="small"
                                column={1}
                                bordered
                                title="Исполнители CRM"
                            >
                                <Descriptions.Item label="Пользователи">
                                    {drawerRecord.executors
                                        ?.length ? (
                                        <Space
                                            direction="vertical"
                                            size={6}
                                            style={{
                                                width: '100%'
                                            }}
                                        >
                                            {drawerRecord.executors.map(
                                                u => {
                                                    const busy = (
                                                        options.users.find(
                                                            x =>
                                                                x.id ===
                                                                u.id
                                                        )
                                                            ?.busyAt ||
                                                        []
                                                    ).filter(
                                                        cp =>
                                                            String(
                                                                cp.id
                                                            ) !==
                                                            String(
                                                                drawerRecord.id
                                                            )
                                                    );
                                                    return (
                                                        <div
                                                            key={
                                                                u.id
                                                            }
                                                        >
                                                            <Tag
                                                                icon={
                                                                    <UserOutlined />
                                                                }
                                                            >
                                                                {u.name}{' '}
                                                                (
                                                                {
                                                                    u.login
                                                                }
                                                                )
                                                            </Tag>
                                                            {busy.length ? (
                                                                <Tag color="orange">
                                                                    также:{' '}
                                                                    {busy
                                                                        .map(
                                                                            cp =>
                                                                                cp.name
                                                                        )
                                                                        .join(
                                                                            ', '
                                                                        )}
                                                                </Tag>
                                                            ) : null}
                                                        </div>
                                                    );
                                                }
                                            )}
                                        </Space>
                                    ) : (
                                        'не заполнены'
                                    )}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider />
                            <Descriptions
                                size="small"
                                column={1}
                                bordered
                                title="Техника"
                            >
                                <Descriptions.Item label="Служебный авто">
                                    {dash(
                                        drawerRecord
                                            .service_vehicle?.name
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="ДГУ большой">
                                    {dash(
                                        drawerRecord
                                            .large_generator?.name
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="ДГУ малый 1">
                                    {dash(
                                        drawerRecord
                                            .small_generator1
                                            ?.name
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="ДГУ малый 2">
                                    {dash(
                                        drawerRecord
                                            .small_generator2
                                            ?.name
                                    )}
                                </Descriptions.Item>
                                <Descriptions.Item label="Личный авто">
                                    {drawerRecord.has_personal_car
                                        ? 'Да'
                                        : 'Нет'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Своё топливо">
                                    {drawerRecord.has_own_fuel
                                        ? 'Да'
                                        : 'Нет'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Расход">
                                    {drawerRecord.fuel_consumption
                                        ? `${drawerRecord.fuel_consumption} л`
                                        : 'не заполнены'}
                                </Descriptions.Item>
                            </Descriptions>

                            <Divider />
                            <Descriptions
                                size="small"
                                column={1}
                                bordered
                                title="Статьи / прайсы / Active Map"
                            >
                                <Descriptions.Item label="Статьи">
                                    {drawerRecord.allowed_articles
                                        ?.length
                                        ? drawerRecord.allowed_articles
                                              .map(
                                                  id =>
                                                      options.articles.find(
                                                          a =>
                                                              a.id ===
                                                              id
                                                      )?.name ||
                                                      id
                                              )
                                              .join(', ')
                                        : 'не заполнены'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Прайсы">
                                    {drawerRecord.price_lists
                                        ?.length
                                        ? drawerRecord.price_lists
                                              .map(
                                                  id =>
                                                      options.priceTypes.find(
                                                          t =>
                                                              t.id ===
                                                              id
                                                      )?.name ||
                                                      id
                                              )
                                              .join(', ')
                                        : 'не заполнены'}
                                </Descriptions.Item>
                                <Descriptions.Item label="Логин Active Map">
                                    {dash(drawerRecord.am_login)}
                                </Descriptions.Item>
                                <Descriptions.Item label="Имя Active Map">
                                    {dash(drawerRecord.am_name)}
                                </Descriptions.Item>
                            </Descriptions>
                        </>
                    ) : null}
                </Drawer>

                <Modal
                    title={
                        editingRecord
                            ? `Редактирование: ${editingRecord.name}`
                            : 'Создание контрагента'
                    }
                    open={modalVisible}
                    onOk={handleFormSubmit}
                    onCancel={() => setModalVisible(false)}
                    width={920}
                    confirmLoading={loading}
                    okText="Сохранить"
                    cancelText="Отмена"
                    style={{ top: 20 }}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Tabs
                            items={[
                                {
                                    key: 'main',
                                    forceRender: true,
                                    label: 'Основное',
                                    children: (
                                        <>
                                            <Row gutter={16}>
                                                <Col span={16}>
                                                    <Form.Item
                                                        name="name"
                                                        label="Название организации / ФИО"
                                                        rules={[
                                                            {
                                                                required: true,
                                                                message:
                                                                    'Обязательно'
                                                            }
                                                        ]}
                                                    >
                                                        <Input placeholder="Введите название" />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        name="type"
                                                        label="Тип"
                                                        rules={[
                                                            {
                                                                required: true
                                                            }
                                                        ]}
                                                    >
                                                        <Select
                                                            options={
                                                                TYPE_OPTIONS
                                                            }
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="allowed_articles"
                                                        label="Разрешенные фин. статьи"
                                                    >
                                                        <Select
                                                            mode="multiple"
                                                            placeholder="Выберите статьи"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.articles.map(
                                                                a => ({
                                                                    value: a.id,
                                                                    label: a.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="price_lists"
                                                        label="Прайс-листы"
                                                    >
                                                        <Select
                                                            mode="multiple"
                                                            placeholder="Выберите типы цен"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.priceTypes.map(
                                                                t => ({
                                                                    value: t.id,
                                                                    label: t.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Form.Item
                                                name="comment"
                                                label="Комментарий"
                                            >
                                                <TextArea
                                                    rows={3}
                                                    showCount
                                                    maxLength={2000}
                                                />
                                            </Form.Item>
                                        </>
                                    )
                                },
                                {
                                    key: 'legal',
                                    forceRender: true,
                                    label: 'Реквизиты',
                                    children: (
                                        <>
                                            {innAlert}
                                            <Row gutter={16}>
                                                <Col span={14}>
                                                    <Form.Item
                                                        name="inn"
                                                        label="ИНН"
                                                    >
                                                        <Input
                                                            placeholder="10 или 12 цифр"
                                                            maxLength={12}
                                                            showCount
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col
                                                    span={10}
                                                    style={{
                                                        paddingTop: 30
                                                    }}
                                                >
                                                    <Space>
                                                        <Button
                                                            icon={
                                                                <SafetyCertificateOutlined />
                                                            }
                                                            loading={
                                                                innChecking
                                                            }
                                                            onClick={() =>
                                                                checkInn(
                                                                    {
                                                                        inn: form.getFieldValue(
                                                                            'inn'
                                                                        ),
                                                                        counterpartyId:
                                                                            editingRecord?.id,
                                                                        fillForm: true
                                                                    }
                                                                )
                                                            }
                                                        >
                                                            Проверить ИНН
                                                        </Button>
                                                    </Space>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col span={8}>
                                                    <Form.Item
                                                        name="kpp"
                                                        label="КПП"
                                                    >
                                                        <Input maxLength={9} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        name="ogrn"
                                                        label="ОГРН / ОГРНИП"
                                                    >
                                                        <Input maxLength={15} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={8}>
                                                    <Form.Item
                                                        name="contract_number"
                                                        label="Договор"
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Form.Item
                                                name="legal_name"
                                                label="Юридическое наименование"
                                            >
                                                <Input
                                                    showCount
                                                    maxLength={512}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                name="legal_address"
                                                label="Юридический адрес"
                                            >
                                                <TextArea
                                                    rows={2}
                                                    showCount
                                                    maxLength={1000}
                                                />
                                            </Form.Item>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="bank_name"
                                                        label="Банк"
                                                    >
                                                        <Input />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="bik"
                                                        label="БИК"
                                                    >
                                                        <Input maxLength={9} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="checking_account"
                                                        label="Расчётный счёт"
                                                    >
                                                        <Input maxLength={34} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="correspondent_account"
                                                        label="Корр. счёт"
                                                    >
                                                        <Input maxLength={34} />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    )
                                },
                                {
                                    key: 'tech',
                                    forceRender: true,
                                    label: 'Техника',
                                    children: (
                                        <div
                                            style={{
                                                background:
                                                    '#fafafa',
                                                padding: 16,
                                                border:
                                                    '1px solid #f0f0f0',
                                                borderRadius: 8
                                            }}
                                        >
                                            <h4 style={{ marginTop: 0 }}>
                                                <CarOutlined /> Техника
                                                и параметры
                                            </h4>
                                            <Row gutter={16}>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="service_vehicle_id"
                                                        label="Служебный авто"
                                                    >
                                                        <Select
                                                            allowClear
                                                            placeholder="Выбрать"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.vehicles.map(
                                                                v => ({
                                                                    value: v.id,
                                                                    label: v.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="large_generator_id"
                                                        label="ДГУ Большой"
                                                    >
                                                        <Select
                                                            allowClear
                                                            placeholder="Выбрать"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.generators.map(
                                                                g => ({
                                                                    value: g.id,
                                                                    label: g.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="small_generator1_id"
                                                        label="ДГУ Малый 1"
                                                    >
                                                        <Select
                                                            allowClear
                                                            placeholder="Выбрать"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.generators.map(
                                                                g => ({
                                                                    value: g.id,
                                                                    label: g.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="small_generator2_id"
                                                        label="ДГУ Малый 2"
                                                    >
                                                        <Select
                                                            allowClear
                                                            placeholder="Выбрать"
                                                            showSearch
                                                            optionFilterProp="label"
                                                            options={options.generators.map(
                                                                g => ({
                                                                    value: g.id,
                                                                    label: g.name
                                                                })
                                                            )}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                            <Row
                                                gutter={16}
                                                align="middle"
                                            >
                                                <Col span={6}>
                                                    <Form.Item
                                                        name="has_personal_car"
                                                        label="Личный авто"
                                                        valuePropName="checked"
                                                    >
                                                        <Switch
                                                            checkedChildren="Да"
                                                            unCheckedChildren="Нет"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={6}>
                                                    <Form.Item
                                                        name="has_own_fuel"
                                                        label="Свое топливо"
                                                        valuePropName="checked"
                                                    >
                                                        <Switch
                                                            checkedChildren="Да"
                                                            unCheckedChildren="Нет"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={12}>
                                                    <Form.Item
                                                        name="fuel_consumption"
                                                        label="Расход топлива (л/100км или л/ч)"
                                                    >
                                                        <InputNumber
                                                            style={{
                                                                width: '100%'
                                                            }}
                                                            step={0.01}
                                                            min={0}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </div>
                                    )
                                },
                                {
                                    key: 'executors',
                                    forceRender: true,
                                    label: 'Исполнители',
                                    children: (
                                        <Form.Item
                                            name="executor_user_ids"
                                            label="Исполнители CRM"
                                            extra="Каждый исполнитель — отдельной строкой. Если уже привязан к другому КА — будет предупреждение."
                                        >
                                            <ExecutorRowsEditor
                                                users={
                                                    options.users ||
                                                    []
                                                }
                                                excludeCounterpartyId={
                                                    editingRecord?.id
                                                }
                                            />
                                        </Form.Item>
                                    )
                                },
                                {
                                    key: 'am',
                                    forceRender: true,
                                    label: 'Active Map',
                                    children: (
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="am_login"
                                                    label="Логин Active Map"
                                                >
                                                    <Input placeholder="Логин в Active Map" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={12}>
                                                <Form.Item
                                                    name="am_name"
                                                    label="Имя в Active Map"
                                                >
                                                    <Input placeholder="Имя объекта в Active Map" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )
                                }
                            ]}
                        />
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default CounterpartiesPage;
